/**
 * usePrayerTimes Hook
 *
 * React hook for fetching and managing prayer times data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPrayerTimes } from '@/services/api/prayerTimesService';
import { cacheService } from '@/services/cache/cacheService';
import type { PrayerTimesResponse } from '@/types/prayer-times.types';

/**
 * Prayer times hook options
 */
export interface UsePrayerTimesOptions {
  companyId?: number;
  autoFetch?: boolean;
  enableCache?: boolean;
  cacheTTL?: number; // Cache time-to-live in milliseconds
  refreshInterval?: number; // Auto-refresh interval in milliseconds
}

/**
 * Prayer times hook result
 */
export interface UsePrayerTimesResult {
  data: PrayerTimesResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch prayer times
 *
 * @param options - Configuration options
 * @returns Prayer times data and control functions
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = usePrayerTimes({
 *   companyId: 123,
 *   refreshInterval: 60000, // Refresh every minute
 * });
 * ```
 */
export function usePrayerTimes(options?: UsePrayerTimesOptions): UsePrayerTimesResult {
  const {
    companyId,
    autoFetch = true,
    enableCache = true,
    cacheTTL = 60 * 60 * 1000, // 1 hour default
    refreshInterval,
  } = options || {};

  const [data, setData] = useState<PrayerTimesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const isFetchingRef = useRef(false);
  const intervalRef = useRef<number | null>(null);

  const fetchPrayerTimes = useCallback(async () => {
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    const cacheKey = `prayer-times:${companyId ?? 'default'}`;

    setIsLoading(true);
    setError(null);
    isFetchingRef.current = true;

    try {
      // Check cache first
      if (enableCache) {
        const cached = cacheService.get<PrayerTimesResponse>(cacheKey);
        if (cached) {
          setData(cached);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }
      }

      // Fetch from API
      const response = await getPrayerTimes(companyId ?? 10001);
      setData(response);

      // Cache the result
      if (enableCache) {
        cacheService.set(cacheKey, response, { ttl: cacheTTL });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch prayer times');
      setError(error);
      console.error('[usePrayerTimes] Error:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [companyId, enableCache, cacheTTL]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      fetchPrayerTimes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, autoFetch]);

  // Auto-refresh interval - stop on error
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up interval if no error and refreshInterval is configured
    if (refreshInterval && refreshInterval > 0 && !error) {
      intervalRef.current = setInterval(() => {
        fetchPrayerTimes();
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refreshInterval, fetchPrayerTimes, error]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchPrayerTimes,
  };
}
